import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GitBranch, Loader2 } from 'lucide-react'
import type {ScmInstallation} from '@/lib/api/features/scm';
import {
  
  listScmInstallations,
  startScmConnect
} from '@/lib/api/features/scm'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

type Props = {
  workspaceSlug: string
}

/**
 * Workspace-level GitHub connection: run the GitHub App OAuth handshake (server-side token
 * minting) and list the connected organisations/accounts. The installation is workspace-scoped
 * and shared by every project — repositories are bound to individual projects from their own
 * settings (see GitHubConnectSection). No secrets or installation ids are trusted from the client.
 */
export function WorkspaceIntegrationsSection({ workspaceSlug }: Props) {
  const { t } = useTranslation('projects')
  const canManage = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_WORKSPACE_SETTINGS)

  const [loading, setLoading] = useState(true)
  const [installations, setInstallations] = useState<Array<ScmInstallation>>([])

  // Toast + clean the ?scm_connected / ?scm_error the callback appended, then load state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('scm_connected')) {
      toast.success(t('github.connectSuccess'))
    } else if (params.has('scm_error')) {
      toast.error(t('github.connectError'))
    }
    if (params.has('scm_connected') || params.has('scm_error')) {
      params.delete('scm_connected')
      params.delete('scm_error')
      const qs = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
    }

    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const inst = await listScmInstallations(workspaceSlug)
        if (!cancelled) setInstallations(inst)
      } catch (err) {
        logger.logError(err as Error, LogCategory.API_ERROR, { context: 'scm_workspace_load' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceSlug])

  const connect = async (purpose?: 'personal') => {
    try {
      const url = await startScmConnect(workspaceSlug, { purpose, returnPath: window.location.pathname })
      window.location.href = url
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      toast.error(status === 503 ? t('github.notConfigured') : t('github.connectError'))
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <h2 className="px-0.5 text-[13px] font-semibold">{t('github.title')}</h2>
      <p className="-mt-1 mb-1 px-0.5 text-xs text-muted-foreground">{t('github.workspaceDescription')}</p>

      <div className="rounded-[14px] border bg-card p-[18px]">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('github.title')}…
          </div>
        ) : installations.length === 0 ? (
          <Button variant="outline" size="sm" onClick={() => connect()} disabled={!canManage}>
            <GitBranch className="size-4" /> {t('github.connect')}
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t('github.connectedAccounts')}</p>
              <ul className="space-y-1.5">
                {installations.map((i) => (
                  <li key={i.installationId} className="flex items-center gap-2 text-sm">
                    <span
                      className={`inline-block size-2 rounded-full ${i.isActive ? 'bg-green-500' : 'bg-amber-500'}`}
                    />
                    <span className="font-medium">{i.accountLogin ?? i.installationId}</span>
                    {!i.isActive && (
                      <Badge variant="outline" className="text-xs">
                        {t('github.suspended')}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {canManage && (
              <Button variant="outline" size="sm" onClick={() => connect()}>
                <GitBranch className="size-4" /> {t('github.connectAnother')}
              </Button>
            )}

            {/* Any member can link their own GitHub account so their PRs are attributed to them. */}
            <div className="flex items-center gap-2 border-t pt-3">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => connect('personal')}>
                <GitBranch className="size-3.5" /> {t('github.linkMine')}
              </Button>
              <span className="text-xs text-muted-foreground">{t('github.linkMineHint')}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
